import { Inject, inject, Injectable } from '@angular/core';
import { Breakpoints } from '@angular/cdk/layout';
import { BehaviorSubject, filter, Observable, Subject } from 'rxjs';
import { MenuItem } from '../interfaces/menuitem.interface';
import { Select, Store } from '@ngxs/store';
import { AuthState } from 'src/app/core/states/auth.state';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { User } from 'src/app/core/models/user.model';

const defaultTopMenuItems: MenuItem[] = [
    {
        label: 'Accueil',
        icon: 'icon-mitoyen',
        routerLink: ['/main'],
        routerLinkActiveOptions: { exact: true },
        hidden: false,
        isActive: false,
        subItems: [],
    },
    {
        label: 'Projets',
        icon: 'bi bi-folder2-open',
        routerLink: ['/main/projects/all'],
        routerLinkActiveOptions: { exact: true },
        hidden: false,
        isActive: false,
        subItems: [],
    },
    {
        label: 'Feuilles de temps',
        icon: 'icon-calendar',
        routerLink: ['/main/timesheet/'],
        routerLinkActiveOptions: { exact: true },
        hidden: false,
        isActive: false,
        subItems: [
            {
                label: `Ma feuille de temps`,
                icon: 'icon-clock-stopwatch',
                routerLink: [`/main/timesheet/`],
                routerLinkActiveOptions: { exact: true },
                hidden: false,
                isActive: false,
                subItems: [],
            },
            {
                label: `Mes contributions`,
                icon: 'icon-calendar',
                routerLink: [`/main/timesheet/contributor/`],
                routerLinkActiveOptions: { exact: true },
                hidden: false,
                isActive: false,
                subItems: [],
            },
        ],
    },
    {
        label: 'Tableau de bord',
        icon: 'icon-bar-chart-10',
        routerLink: ['/main/dashboard/billing'],
        routerLinkActiveOptions: { exact: true },
        hidden: false,
        isActive: false,
        subItems: [
            {
                label: 'Facturation',
                icon: 'bi bi-receipt',
                routerLink: ['/main/dashboard/billing'],
                routerLinkActiveOptions: { exact: true },
                hidden: false,
                isActive: false,
                subItems: [],
            },
            {
                label: 'Analyse du temps',
                icon: 'icon-clock-stopwatch',
                routerLink: ['/main/dashboard/timesheet'],
                routerLinkActiveOptions: { exact: true },
                hidden: false,
                isActive: false,
                subItems: [],
            },
        ],
    },
];

const defaultBottomMenuItems: MenuItem[] = [
    {
        label: 'Mon profil',
        icon: 'icon-user-01',
        routerLink: ['/main/account'],
        routerLinkActiveOptions: { exact: true },
        isActive: false,
        panelOpenState: false,
        subItems: [],
    },
];
const adminMenuItem: MenuItem = {
    label: 'Administration',
    icon: 'icon-tool-01',
    routerLink: ['/admin/organization'],
    routerLinkActiveOptions: { exact: true },
    isActive: false,
    subItems: [],
};

@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
export class SidenavService {
    public loggedUser$: Observable<User | null> = inject(Store).select(AuthState.loggedUser);
    public toggleSidenavObs = new BehaviorSubject<boolean>(false);
    public toggleSidenavObs$: Observable<boolean> = this.toggleSidenavObs.asObservable();

    public breakPointsThreshold = [Breakpoints.XSmall, Breakpoints.Small];

    private topMenuItemsObs = new BehaviorSubject<MenuItem[]>(defaultTopMenuItems);
    public topMenuItems$: Observable<MenuItem[]> = this.topMenuItemsObs.asObservable();
    private bottomMenuItemsObs = new BehaviorSubject<MenuItem[]>(defaultBottomMenuItems);
    public bottomMenuItems$: Observable<MenuItem[]> = this.bottomMenuItemsObs.asObservable();

    constructor() {
        this.loggedUser$
            .pipe(filter(Boolean), untilDestroyed(this))
            .subscribe((loggedUser: User) => {
                const bottomMenuItems: MenuItem[] = [...defaultBottomMenuItems];
                if (loggedUser.isAdmin) {
                    bottomMenuItems.push(adminMenuItem);
                }
                this.bottomMenuItemsObs.next(bottomMenuItems);
            });
    }

    public toggle() {
        this.toggleSidenavObs.next(!this.toggleSidenavObs.value);
    }

    public resetSubMenuItem(label: string, subItems: MenuItem[]) {
        const topMenuItems = this.topMenuItemsObs.getValue();
        this.topMenuItemsObs.next(
            topMenuItems.map((menu: MenuItem) => {
                if (menu.label === label) {
                    menu.subItems = subItems;
                }
                return menu;
            }),
        );
    }
    public setTopMenuItems(menuItems: MenuItem[]) {
        this.topMenuItemsObs.next([...defaultTopMenuItems, ...menuItems]);
    }
}
